
import { defineComponent } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { getGhlAiSettings, updateGhlAiSettings, getGhlAiUsage } from "@/api/ghlAi.api.js";
import {
  getCommentDmCustomValue,
  updateCommentDmCustomValue,
  getAutoCommentCustomValues,
  updateAutoCommentCustomValues,
  getCheckoutNurtureSettings,
  updateCheckoutNurtureSettings
} from "@/api/ghl.api.js";
import { fetchGhlPaymentCatalogList } from "@/api/ghlPayment.api.js";
import AiDmOnboarding from "@/components/ai/AiDmOnboarding.vue";
import { getActiveInstance } from "@/utils/ghlInstance.js";
import {
  getCachedAutoComments,
  getCachedCommentDm,
  getCachedSectionVisibility,
  setCachedAutoComments,
  setCachedCommentDm,
  setCachedSectionVisibility
} from "@/utils/ghlCustomValuesCache.js";

const GHL_APP_BASE_URL = (process.env.VUE_APP_GHL_APP_BASE_URL || "https://app.ai247saas.com").replace(/\/$/, "");
const AI_CREDIT_TOP_UP_SKU = "857";
const COMMENT_DM_MAX_LENGTH = 1000;
const AUTO_COMMENT_SLOT_COUNT = 5;
const AUTO_COMMENT_MAX_LENGTH = 1000;

export default defineComponent({
  name: "ai-settings",
  components: {
    AiDmOnboarding
  },
  props: {
    hasGhlLocation: {
      type: Boolean,
      default: false
    },
    locationId: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      loading: true,
      saving: false,
      completingOnboarding: false,
      resettingOnboarding: false,
      aiEnabled: false,
      globalAiEnabled: false,
      dmOnboardingCompleted: false,
      canEnableAi: false,
      ghlIntegrationsUrl: "",
      defaultAgentId: null as number | null,
      availableAgents: [] as any[],
      loadError: "",
      usageLoaded: false,
      usageError: "",
      usageSummary: {
        chatReplyCount: 0
      },
      usageByEventType: [] as any[],
      usagePeriodStart: "",
      usagePeriodEnd: "",
      usageCredits: {
        displayCreditsRemaining: 0,
        displayCreditsUsedInPeriod: 0,
        creditsDepleted: false
      },
      topUpLoading: false,
      commentDmValue: "",
      commentDmSavedValue: "",
      commentDmLoading: false,
      commentDmSaving: false,
      commentDmError: "",
      commentDmMaxLength: COMMENT_DM_MAX_LENGTH,
      autoCommentValues: Array(AUTO_COMMENT_SLOT_COUNT).fill(""),
      autoCommentSavedValues: Array(AUTO_COMMENT_SLOT_COUNT).fill(""),
      autoCommentLoading: false,
      autoCommentSaving: false,
      autoCommentError: "",
      autoCommentMaxLength: AUTO_COMMENT_MAX_LENGTH,
      checkoutNurtureEnabled: false,
      checkoutNurtureLoading: false,
      checkoutNurtureSaving: false,
      checkoutNurtureError: "",
      sectionVisibility: {
        commentDm: true,
        autoComments: true,
        checkoutRecovery: true
      }
    };
  },
  computed: {
    commentDmDirty(): boolean {
      return this.commentDmValue.trim() !== this.commentDmSavedValue.trim();
    },
    autoCommentDirty(): boolean {
      return this.autoCommentValues.some(
        (value, index) => value.trim() !== this.autoCommentSavedValues[index].trim()
      );
    },
    usagePeriodLabel(): string {
      if (!this.usagePeriodStart || !this.usagePeriodEnd) {
        return "";
      }
      const start = new Date(this.usagePeriodStart);
      const end = new Date(this.usagePeriodEnd);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return "";
      }
      const formatter = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
      return `${formatter.format(start)} – ${formatter.format(end)}`;
    },
    resolvedIntegrationsUrl(): string {
      if (this.ghlIntegrationsUrl) {
        return this.ghlIntegrationsUrl;
      }
      if (!this.locationId) {
        return "";
      }
      return `${GHL_APP_BASE_URL}/v2/location/${this.locationId}/settings/lc-integrations`;
    }
  },
  watch: {
    hasGhlLocation: {
      immediate: false,
      handler(value: boolean) {
        if (value) {
          this.loadSectionVisibility();
          this.loadCommentDm();
          this.loadAutoComments();
          this.loadCheckoutNurture();
        }
      }
    },
    locationId: {
      immediate: false,
      handler() {
        this.loadSectionVisibility();
      }
    }
  },
  async mounted() {
    this.loadSectionVisibility();
    await Promise.all([
      this.loadSettings(),
      this.loadUsage(),
      this.loadCommentDm(),
      this.loadAutoComments(),
      this.loadCheckoutNurture()
    ]);
  },
  methods: {
    loadSectionVisibility() {
      this.sectionVisibility = getCachedSectionVisibility(this.locationId);
    },
    persistSectionVisibility() {
      setCachedSectionVisibility(this.locationId, this.sectionVisibility);
    },
    formatNumber(value: number) {
      return new Intl.NumberFormat().format(Number(value) || 0);
    },
    formatEventType(eventType: string) {
      const labels: Record<string, string> = {
        chat_reply: "Chat replies",
        embed_query: "RAG queries",
        embed_index: "KB indexing",
        conversation_summary: "Conversation summaries",
        follow_up: "Follow-up messages",
        image_read: "Image reading",
        social_caption_draft: "Post caption drafts",
        social_image_generate: "Post image generation",
        social_image_moderate: "Post image moderation",
        social_image_prompt: "Post image prompt helper",
        reel_script: "Reel script generation",
        reel_generate: "Reel video generation",
        fanout_caption_rewrite: "Caption rewrites",
        fanout_content_moderation: "Content moderation",
        playground_chat: "Playground chats"
      };
      return labels[eventType] || eventType;
    },
    applyUsageData(data: Record<string, unknown>) {
      const summary = (data.summary || {}) as Record<string, unknown>;
      this.usageSummary = {
        chatReplyCount: Number(summary.chatReplyCount) || 0
      };
      this.usageByEventType = Array.isArray(data.byEventType) ? data.byEventType : [];
      this.usagePeriodStart = String(data.periodStart || "");
      this.usagePeriodEnd = String(data.periodEnd || "");
      const credits = (data.credits || {}) as Record<string, unknown>;
      this.usageCredits = {
        displayCreditsRemaining: Number(credits.displayCreditsRemaining) || 0,
        displayCreditsUsedInPeriod: Number(credits.displayCreditsUsedInPeriod) || 0,
        creditsDepleted: Boolean(credits.creditsDepleted)
      };
      this.usageLoaded = true;
    },
    async loadUsage() {
      this.usageError = "";
      try {
        const response = await getGhlAiUsage();
        this.applyUsageData(response.data || {});
      } catch (err) {
        this.usageLoaded = false;
        this.usageError = (err as any)?.message || "Failed to load AI usage.";
      }
    },
    applySettingsData(data: Record<string, unknown>) {
      this.aiEnabled = Boolean(data.aiEnabled);
      this.globalAiEnabled = Boolean(data.globalAiEnabled);
      this.dmOnboardingCompleted = Boolean(data.dmOnboardingCompleted);
      this.canEnableAi = Boolean(data.canEnableAi);
      this.ghlIntegrationsUrl = String(data.ghlIntegrationsUrl || "");
      this.defaultAgentId = data.defaultAgentId
        ? Number(data.defaultAgentId)
        : null;
      this.availableAgents = Array.isArray(data.availableAgents)
        ? data.availableAgents
        : [];
    },
    applyCommentDmValue(value: string) {
      const normalized = String(value || "").trim();
      this.commentDmValue = normalized;
      this.commentDmSavedValue = normalized;
    },
    async loadCommentDm() {
      if (!this.hasGhlLocation) {
        return;
      }

      const cached = getCachedCommentDm(this.locationId);
      if (cached !== null) {
        this.applyCommentDmValue(cached);
        return;
      }

      this.commentDmLoading = true;
      this.commentDmError = "";
      try {
        const response = await getCommentDmCustomValue();
        const value = String(response.data?.value || "").trim();
        this.applyCommentDmValue(value);
        setCachedCommentDm(this.locationId, value);
      } catch (err) {
        this.commentDmError = (err as any)?.response?.data?.message
          || (err as any)?.message
          || "Failed to load comment DM message.";
      } finally {
        this.commentDmLoading = false;
      }
    },
    async saveCommentDm() {
      const value = this.commentDmValue.trim();
      if (!value) {
        Swal.fire({
          icon: "warning",
          title: "Message required",
          text: "Enter a message before saving."
        });
        return;
      }

      this.commentDmSaving = true;
      this.commentDmError = "";
      try {
        const response = await updateCommentDmCustomValue(value);
        const saved = String(response.data?.value || value).trim();
        this.applyCommentDmValue(saved);
        setCachedCommentDm(this.locationId, saved);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Comment DM message saved",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        this.commentDmError = (err as any)?.response?.data?.message
          || (err as any)?.message
          || "Failed to save comment DM message.";
      } finally {
        this.commentDmSaving = false;
      }
    },
    applyAutoCommentValues(values: Array<{ value?: string } | string>) {
      const normalized = Array.from({ length: AUTO_COMMENT_SLOT_COUNT }, (_, index) => {
        const entry = values[index];
        if (typeof entry === "string") {
          return entry.trim();
        }
        return String(entry?.value || "").trim();
      });
      this.autoCommentValues = [...normalized];
      this.autoCommentSavedValues = [...normalized];
    },
    async loadAutoComments() {
      if (!this.hasGhlLocation) {
        return;
      }

      const cached = getCachedAutoComments(this.locationId);
      if (cached) {
        this.applyAutoCommentValues(cached);
        return;
      }

      this.autoCommentLoading = true;
      this.autoCommentError = "";
      try {
        const response = await getAutoCommentCustomValues();
        const values = Array.isArray(response.data?.values) ? response.data.values : [];
        this.applyAutoCommentValues(values);
        setCachedAutoComments(this.locationId, this.autoCommentValues);
      } catch (err) {
        this.autoCommentError = (err as any)?.response?.data?.message
          || (err as any)?.message
          || "Failed to load auto-comment messages.";
      } finally {
        this.autoCommentLoading = false;
      }
    },
    async loadCheckoutNurture() {
      if (!this.hasGhlLocation) {
        return;
      }

      this.checkoutNurtureLoading = true;
      this.checkoutNurtureError = "";
      try {
        const response = await getCheckoutNurtureSettings();
        this.checkoutNurtureEnabled = Boolean(response.data?.enabled);
      } catch (err) {
        this.checkoutNurtureError = (err as any)?.response?.data?.message
          || (err as any)?.message
          || "Failed to load checkout recovery settings.";
      } finally {
        this.checkoutNurtureLoading = false;
      }
    },
    async saveCheckoutNurture() {
      this.checkoutNurtureSaving = true;
      this.checkoutNurtureError = "";
      try {
        const response = await updateCheckoutNurtureSettings(this.checkoutNurtureEnabled);
        this.checkoutNurtureEnabled = Boolean(response.data?.enabled);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: this.checkoutNurtureEnabled
            ? "Checkout recovery enabled"
            : "Checkout recovery disabled",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        this.checkoutNurtureError = (err as any)?.response?.data?.message
          || (err as any)?.message
          || "Failed to save checkout recovery settings.";
        await this.loadCheckoutNurture();
      } finally {
        this.checkoutNurtureSaving = false;
      }
    },
    async saveAutoComments() {
      const values = this.autoCommentValues.map((value) => value.trim());
      const emptyIndex = values.findIndex((value) => !value);
      if (emptyIndex !== -1) {
        Swal.fire({
          icon: "warning",
          title: "All replies required",
          text: `Enter a message for reply ${emptyIndex + 1} before saving.`
        });
        return;
      }

      this.autoCommentSaving = true;
      this.autoCommentError = "";
      try {
        const response = await updateAutoCommentCustomValues(values);
        const savedValues = Array.isArray(response.data?.values) ? response.data.values : [];
        this.applyAutoCommentValues(savedValues.length ? savedValues : values);
        setCachedAutoComments(this.locationId, this.autoCommentValues);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Auto-comment replies saved",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        this.autoCommentError = (err as any)?.response?.data?.message
          || (err as any)?.message
          || "Failed to save auto-comment messages.";
      } finally {
        this.autoCommentSaving = false;
      }
    },
    async loadSettings() {
      this.loading = true;
      this.loadError = "";
      try {
        const response = await getGhlAiSettings();
        this.applySettingsData(response.data || {});
      } catch (err) {
        this.loadError = (err as any)?.message || "Failed to load AI settings.";
      } finally {
        this.loading = false;
      }
    },
    async completeOnboarding() {
      this.completingOnboarding = true;
      try {
        const response = await updateGhlAiSettings({
          dmOnboardingCompleted: true,
          aiEnabled: false
        });
        this.applySettingsData(response.data || {});
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Messenger setup marked complete",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not save onboarding status",
          text: (err as any)?.message || "Please try again."
        });
      } finally {
        this.completingOnboarding = false;
      }
    },
    async resetOnboarding() {
      this.resettingOnboarding = true;
      try {
        const response = await updateGhlAiSettings({
          dmOnboardingCompleted: false,
          aiEnabled: false
        });
        this.applySettingsData(response.data || {});
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not update onboarding status",
          text: (err as any)?.message || "Please try again."
        });
      } finally {
        this.resettingOnboarding = false;
      }
    },
    async startCreditTopUp() {
      if (this.topUpLoading) return;

      this.topUpLoading = true;
      try {
        const { data } = await fetchGhlPaymentCatalogList();
        if (!data?.success) {
          throw new Error(data?.message || "Product catalog is unavailable.");
        }

        const locationId = String(data.locationId || "").trim();
        if (!locationId) {
          throw new Error("Product catalog location is not configured.");
        }

        const products = Array.isArray(data.products) ? data.products : [];
        const hasTopUpProduct = products.some(
          (product: { sku?: string | number }) => String(product?.sku || "") === AI_CREDIT_TOP_UP_SKU
        );
        if (!hasTopUpProduct) {
          throw new Error("AI credit top-up product is not available right now.");
        }

        const query: Record<string, string> = {
          locationId,
          sku: AI_CREDIT_TOP_UP_SKU
        };
        const ecpid = getActiveInstance();
        if (ecpid) {
          query.ecpid = ecpid;
        }

        await this.$router.push({ path: "/pay", query });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not start checkout",
          text: (err as any)?.response?.data?.message || (err as any)?.message || "Please try again."
        });
      } finally {
        this.topUpLoading = false;
      }
    },
    async saveSettings() {
      if (!this.hasGhlLocation || !this.dmOnboardingCompleted) {
        this.aiEnabled = false;
        return;
      }

      if (this.aiEnabled && !this.defaultAgentId) {
        this.aiEnabled = false;
        Swal.fire({
          icon: "warning",
          title: "Select an AI agent",
          text: "Choose an AI agent before enabling AI auto-reply."
        });
        return;
      }

      this.saving = true;
      try {
        const response = await updateGhlAiSettings({
          aiEnabled: this.aiEnabled,
          dmOnboardingCompleted: this.dmOnboardingCompleted,
          defaultAgentId: this.defaultAgentId
        });
        this.applySettingsData(response.data || {});
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: this.aiEnabled ? "AI auto-reply enabled" : "AI settings saved",
          showConfirmButton: false,
          timer: 2000
        });
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Could not update AI settings",
          text: (err as any)?.response?.data?.message || (err as any)?.message || "Please try again."
        });
        await this.loadSettings();
      } finally {
        this.saving = false;
      }
    }
  }
});
